/**
 * Language hook
 * Provides language management functionality
 */

// #region Imports
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nService, SupportedLanguage, LanguageConfig } from '@/services/i18n';
// #endregion

// #region Types
/**
 * Return type for useLanguage hook
 */
export interface UseLanguageReturn {
    /** Current language code */
    currentLanguage: SupportedLanguage;
    /** Available language configurations */
    availableLanguages: LanguageConfig[];
    /** Change language function */
    changeLanguage: (language: SupportedLanguage) => Promise<void>;
    /** Get language configuration by code */
    getLanguageConfig: (code: SupportedLanguage) => LanguageConfig;
    /** Check if language is supported */
    isSupportedLanguage: (code: string) => code is SupportedLanguage;
    /** Whether a language change is in progress */
    isChangingLanguage: boolean;
}
// #endregion

// #region Hook
/**
 * Language management hook
 * Provides utilities for language switching and configuration
 *
 * @returns Language management utilities
 *
 * @example
 * ```tsx
 * function LanguageSelector() {
 *   const { currentLanguage, availableLanguages, changeLanguage, isChangingLanguage } = useLanguage();
 *
 *   return (
 *     <select
 *       value={currentLanguage}
 *       onChange={(e) => changeLanguage(e.target.value as SupportedLanguage)}
 *       disabled={isChangingLanguage}
 *     >
 *       {availableLanguages.map(lang => (
 *         <option key={lang.code} value={lang.code}>
 *           {lang.nativeName}
 *         </option>
 *       ))}
 *     </select>
 *   );
 * }
 * ```
 */
export function useLanguage(): UseLanguageReturn {
    const { i18n } = useTranslation();
    const [isChangingLanguage, setIsChangingLanguage] = useState(false);

    // Get current language from i18n
    const currentLanguage = I18nService.getCurrentLanguage();

    // Get available languages
    const availableLanguages = I18nService.getAvailableLanguages();

    // Change language function
    const changeLanguage = useCallback(async (language: SupportedLanguage) => {
        setIsChangingLanguage(true);
        try {
            await I18nService.changeLanguage(language);
        } finally {
            setIsChangingLanguage(false);
        }
    }, []);

    // Get language configuration by code
    const getLanguageConfig = useCallback((code: SupportedLanguage) => {
        return I18nService.getLanguageConfig(code);
    }, []);

    // Check if language is supported
    const isSupportedLanguage = useCallback((code: string): code is SupportedLanguage => {
        return I18nService.isSupportedLanguage(code);
    }, []);

    return {
        currentLanguage,
        availableLanguages,
        changeLanguage,
        getLanguageConfig,
        isSupportedLanguage,
        isChangingLanguage,
    };
}
// #endregion
